import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { mapCompanyPortalData } from './workspaceBranding';
import {
  normalizeActiveModules,
  normalizeCustomAiModules,
} from './companyActiveModules';
import { normalizeRolePermissions } from './rolePermissions';

export const MODULE_KEYS = ['ticketing', 'externalLinks', 'financeOps', 'aiInsights'];

/** @type {Record<string, boolean>} */
export const DEFAULT_ENABLED_MODULES = {
  ticketing: true,
  externalLinks: true,
  financeOps: false,
  aiInsights: false,
};

/**
 * Merge enabled module flags with defaults.
 * @param {Record<string, unknown> | null | undefined} raw
 * @returns {Record<string, boolean>}
 */
export function normalizeEnabledModules(raw) {
  const out = { ...DEFAULT_ENABLED_MODULES };
  if (!raw || typeof raw !== 'object') return out;
  for (const key of MODULE_KEYS) {
    if (typeof raw[key] === 'boolean') out[key] = raw[key];
  }
  return out;
}

/** General company information fields editable from Settings. */
export const COMPANY_PROFILE_FIELDS = [
  'legalName',
  'website',
  'contactEmail',
  'contactPhone',
  'taxId',
  'description',
  'addressLine1',
  'addressLine2',
  'city',
  'stateRegion',
  'postalCode',
  'country',
];

/**
 * @param {unknown} value
 * @returns {string}
 */
function readString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Allowed GitHub org login names for AI Factory / GitOps boundaries.
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeAllowedGithubOrgs(raw) {
  if (Array.isArray(raw)) {
    return [
      ...new Set(
        raw
          .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
          .filter(Boolean),
      ),
    ];
  }
  if (typeof raw === 'string' && raw.trim()) {
    return [
      ...new Set(
        raw
          .split(/[,;\s]+/)
          .map((entry) => entry.trim())
          .filter(Boolean),
      ),
    ];
  }
  return [];
}

/**
 * @param {unknown} raw
 * @returns {number | null}
 */
export function normalizeMaxAiAgents(raw) {
  if (raw === '' || raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/**
 * Pull the editable general-info fields out of a raw company doc.
 * Falls back to a nested `profile` map when present.
 * @param {Record<string, unknown> | null | undefined} d
 * @returns {Record<string, string>}
 */
export function normalizeCompanyProfile(d) {
  const source = d && typeof d === 'object' ? d : {};
  const nested =
    source.profile && typeof source.profile === 'object'
      ? /** @type {Record<string, unknown>} */ (source.profile)
      : {};
  /** @type {Record<string, string>} */
  const out = {};
  for (const key of COMPANY_PROFILE_FIELDS) {
    out[key] = readString(nested[key]) || readString(source[key]);
  }
  return out;
}

/**
 * Normalize Firestore `companies/{id}` into workspace context value
 * (branding + modular features + AI context).
 * @param {Record<string, unknown>} d
 * @param {string} [id]
 */
export function mapCompanyWorkspaceData(d, id) {
  if (!d || typeof d !== 'object') return null;
  const portal = mapCompanyPortalData(d);
  const niche =
    typeof d.niche === 'string' && d.niche.trim()
      ? d.niche.trim()
      : typeof d.industryNiche === 'string'
        ? d.industryNiche.trim()
        : '';
  const size =
    typeof d.size === 'string' && d.size.trim()
      ? d.size.trim()
      : typeof d.companySize === 'string'
        ? d.companySize.trim()
        : '';

  return {
    ...portal,
    id: id || (typeof d.id === 'string' ? d.id : ''),
    niche,
    size,
    profile: normalizeCompanyProfile(d),
    enabledModules: normalizeEnabledModules(
      /** @type {Record<string, unknown>} */ (d.enabledModules),
    ),
    activeModules: normalizeActiveModules(
      /** @type {Record<string, unknown>} */ (d.activeModules),
    ),
    customAiModules: normalizeCustomAiModules(d.customAiModules),
    rolePermissions: normalizeRolePermissions(
      /** @type {Record<string, unknown>} */ (d.rolePermissions),
    ),
    billing:
      d.billing && typeof d.billing === 'object'
        ? /** @type {Record<string, unknown>} */ (d.billing)
        : null,
    subscriptionPlan:
      typeof d.subscriptionPlan === 'string' ? d.subscriptionPlan : null,
    planName: typeof d.planName === 'string' ? d.planName : null,
    plan: typeof d.plan === 'string' ? d.plan : null,
    aiContext:
      d.aiContext && typeof d.aiContext === 'object'
        ? /** @type {Record<string, unknown>} */ (d.aiContext)
        : null,
    isActive: d.isActive !== false,
    forceTicketAppSelection: d.forceTicketAppSelection === true,
    defaultTriageAgentId:
      typeof d.defaultTriageAgentId === 'string' && d.defaultTriageAgentId.trim()
        ? d.defaultTriageAgentId.trim()
        : null,
    profileInitializedAt: d.profileInitializedAt ?? null,
    displayTimeZone:
      typeof d.displayTimeZone === 'string' && d.displayTimeZone.trim()
        ? d.displayTimeZone.trim()
        : null,
    allowedGithubOrgs: normalizeAllowedGithubOrgs(d.allowedGithubOrgs),
    maxAiAgents: normalizeMaxAiAgents(d.maxAiAgents),
    requireTenantApiKeys: d.requireTenantApiKeys === true,
    assetGenerationProcessingMode:
      d.assetGenerationProcessingMode === 'parallel_batch'
        ? 'parallel_batch'
        : 'sequential',
    aiSpendCap: (() => {
      const settings =
        d.settings && typeof d.settings === 'object' ? d.settings : null;
      const cap =
        settings && settings.aiSpendCap && typeof settings.aiSpendCap === 'object'
          ? settings.aiSpendCap
          : null;
      return cap;
    })(),
    maxRejectionCount: (() => {
      const settings =
        d.settings && typeof d.settings === 'object' ? d.settings : null;
      if (!settings) return null;
      const n =
        typeof settings.maxRejectionCount === 'number'
          ? settings.maxRejectionCount
          : Number(settings.maxRejectionCount);
      if (!Number.isFinite(n)) return null;
      const floored = Math.floor(n);
      if (floored < 1 || floored > 20) return null;
      return floored;
    })(),
  };
}

/** @param {ReturnType<typeof mapCompanyWorkspaceData>} workspace */
export function isModuleEnabled(workspace, moduleKey) {
  return Boolean(workspace?.enabledModules?.[moduleKey]);
}

export { isActiveModuleEnabled } from './companyActiveModules';

const DEFAULT_CHAT_SETTINGS = {
  chatEnabled: false,
  primaryBrandColor: '#D85A30',
  welcomeMessage: '',
  webhookUrl: '',
  assignedAgents: [],
  quickReplies: [],
};

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
function normalizeAssignedAgents(raw) {
  if (!Array.isArray(raw)) return [];
  return [
    ...new Set(
      raw
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean),
    ),
  ];
}

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
function normalizeQuickReplies(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((line) => (typeof line === 'string' ? line.trim() : ''))
    .filter(Boolean);
}

/**
 * Load chat widget settings for a company.
 * Enablement is driven by `activeModules.chatWidget` (legacy: `chatEnabled`).
 * @param {string} companyId
 */
export async function getChatSettings(companyId) {
  const id = typeof companyId === 'string' ? companyId.trim() : '';
  if (!id) return { ...DEFAULT_CHAT_SETTINGS };

  const snap = await getDoc(doc(db, 'companies', id));
  if (!snap.exists()) return { ...DEFAULT_CHAT_SETTINGS };

  const data = snap.data() || {};
  const activeModules = normalizeActiveModules(data.activeModules);
  const embedded =
    data.chatSettings && typeof data.chatSettings === 'object'
      ? data.chatSettings
      : {};

  const chatEnabled =
    activeModules.chatWidget === true ||
    data.chatEnabled === true ||
    embedded.chatEnabled === true ||
    embedded.enabled === true;

  return {
    chatEnabled,
    primaryBrandColor:
      typeof embedded.primaryBrandColor === 'string' && embedded.primaryBrandColor.trim()
        ? embedded.primaryBrandColor.trim()
        : DEFAULT_CHAT_SETTINGS.primaryBrandColor,
    welcomeMessage:
      typeof embedded.welcomeMessage === 'string'
        ? embedded.welcomeMessage
        : '',
    webhookUrl:
      typeof embedded.webhookUrl === 'string' ? embedded.webhookUrl : '',
    assignedAgents: normalizeAssignedAgents(embedded.assignedAgents),
    quickReplies: normalizeQuickReplies(embedded.quickReplies),
  };
}

/**
 * Persist chat widget settings.
 * Writes `activeModules.chatWidget` (canonical) and mirrors legacy `chatEnabled`.
 * Branding fields live under `chatSettings`.
 * @param {string} companyId
 * @param {object} settings
 */
export async function saveChatSettings(companyId, settings) {
  const id = typeof companyId === 'string' ? companyId.trim() : '';
  if (!id) {
    throw new Error('companyId is required.');
  }

  const chatEnabled = settings?.chatEnabled === true;
  const primaryBrandColor =
    typeof settings?.primaryBrandColor === 'string' && settings.primaryBrandColor.trim()
      ? settings.primaryBrandColor.trim()
      : DEFAULT_CHAT_SETTINGS.primaryBrandColor;
  const welcomeMessage =
    typeof settings?.welcomeMessage === 'string' ? settings.welcomeMessage : '';
  const webhookUrl =
    typeof settings?.webhookUrl === 'string' ? settings.webhookUrl : '';
  const assignedAgents = normalizeAssignedAgents(settings?.assignedAgents);
  const quickReplies = normalizeQuickReplies(settings?.quickReplies);

  const companyRef = doc(db, 'companies', id);
  const snap = await getDoc(companyRef);
  if (!snap.exists()) {
    throw new Error(`Company ${id} was not found.`);
  }
  const existing = snap.data() || {};
  const nextActiveModules = {
    ...normalizeActiveModules(existing.activeModules),
    chatWidget: chatEnabled,
  };

  // Dot-path update keeps other activeModules flags intact.
  await updateDoc(companyRef, {
    'activeModules.chatWidget': chatEnabled,
    chatEnabled,
    chatSettings: {
      ...(existing.chatSettings && typeof existing.chatSettings === 'object'
        ? existing.chatSettings
        : {}),
      primaryBrandColor,
      welcomeMessage,
      webhookUrl,
      chatEnabled,
      enabled: chatEnabled,
      assignedAgents,
      quickReplies,
    },
    updatedAt: serverTimestamp(),
  });

  return { success: true, activeModules: nextActiveModules, assignedAgents };
}
